import { jsonInit, toSearchParams } from '@shigo-ui/api-client';
import { z } from 'zod';

import { createShigoApiClient, type ShigoApiConfig } from '../../../lib/shigo-api-client';
import { statusCountsSchema } from '../../../lib/statusCounts';
import {
  networkMutationResponseSchema,
  networkSchema,
  networksPageSchema,
  type NetworkBrandAssetFiles,
  type NetworkMutationPayload,
  type NetworksFilter,
} from './model';

interface NetworksClientOptions {
  config: ShigoApiConfig;
  accessToken: string;
  fetcher?: typeof fetch;
}

const emptyResponseSchema = z.preprocess(
  (value) => (value === '' || value === null ? undefined : value),
  z.void(),
);

export function createNetworksClient({ config, accessToken, fetcher }: NetworksClientOptions) {
  const api = createShigoApiClient(config, accessToken, fetcher);
  return {
    getStatusCounts: (searchTerm: string) =>
      api.request({
        path: `/api/admin/networks/statuses?${new URLSearchParams({ searchTerm: searchTerm.trim() })}`,
        schema: statusCountsSchema,
      }),
    list: (filter: NetworksFilter) =>
      api.request({
        path: `/api/admin/networks?${serializeNetworksFilter(filter)}`,
        schema: networksPageSchema,
      }),
    get: (id: string) =>
      api.request({
        path: `/api/admin/networks/${encodeURIComponent(id)}`,
        schema: networkSchema,
      }),
    create: (payload: NetworkMutationPayload, assets: NetworkBrandAssetFiles) =>
      api.request({
        path: '/api/admin/networks',
        schema: networkMutationResponseSchema,
        init: networkMultipartInit(NetworkWriteMethod.Create, payload, assets),
      }),
    update: (id: string, payload: NetworkMutationPayload, assets: NetworkBrandAssetFiles) =>
      api.request({
        path: `/api/admin/networks/${encodeURIComponent(id)}`,
        schema: networkMutationResponseSchema,
        init: networkMultipartInit(NetworkWriteMethod.Update, payload, assets),
      }),
    setStatus: (id: string, active: boolean) =>
      api.request({
        path: `/api/admin/networks/${encodeURIComponent(id)}/${active ? 'activate' : 'archive'}`,
        schema: emptyResponseSchema,
        init: jsonInit('PUT', {}),
      }),
  };
}

export function serializeNetworksFilter(filter: NetworksFilter) {
  return toSearchParams({
    page: filter.page,
    size: filter.size,
    searchTerm: filter.searchTerm,
    status: filter.statuses,
    ...(filter.sortProperty
      ? { sortProperty: filter.sortProperty, sortDirection: filter.sortDirection }
      : {}),
  }).toString();
}

enum NetworkWriteMethod {
  Create = 'POST',
  Update = 'PUT',
}

function networkMultipartInit(
  method: NetworkWriteMethod,
  payload: NetworkMutationPayload,
  assets: NetworkBrandAssetFiles,
): RequestInit {
  const body = new FormData();
  body.append(
    method === NetworkWriteMethod.Create ? 'createRequest' : 'updateRequest',
    new Blob([JSON.stringify(payload)], { type: 'application/json' }),
  );
  if (assets.logo) body.append('logo', assets.logo);
  if (assets.logoLight) body.append('logoLight', assets.logoLight);
  return { method, body };
}
